import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import React from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  ResponsiveContainer,
  ComposedChart,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

export default function Temperature({ data, projection, unit }) {
  const patientReducer = useSelector((state) => state.patientReducer);
  const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'

  const buildGynecologyTemperatureData = (
    userData,
    projection,
    unit
  ) => {
    if (!projection?.length) return [];

    if (!userData?.length) {
      const startDate = dayjs();

      return projection.map((item, index) => ({
          fullDate: startDate.add(index * 7, "day").valueOf(),
          range: [item.min, item.max],
          week: item.week,
      }));
  }

    const anchorRecord = [...userData].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )[0];

    const anchorWeek = Number(anchorRecord.week);
    const anchorDate = dayjs(anchorRecord.createdAt);

    const projectionMap = {};

    projection.forEach((item) => {
      const diffWeeks = Number(item.week) - anchorWeek;

      const projectionDate = anchorDate
        .add(diffWeeks * 7, "day")
        .startOf("day");

      projectionMap[projectionDate.format("YYYY-MM-DD")] = {
        fullDate: projectionDate.valueOf(),
        range: [item.min, item.max],
        week: item.week,
      };
    });

    userData.forEach((item) => {
      const key = dayjs(item.createdAt).format("YYYY-MM-DD");

      if (!projectionMap[key]) {
        projectionMap[key] = {
          fullDate: dayjs(item.createdAt)
            .startOf("day")
            .valueOf(),
        };
      }

      projectionMap[key].Temp =
        unit === "F"
          ? item.temp
          : item.tempCelsius;
    });

    return Object.values(projectionMap).sort(
      (a, b) => a.fullDate - b.fullDate
    );
  };

  const mergeRange = (userData, projection) => {
    const firstProjectionWeek = projection?.[0]?.week;

    const normalizedUserData = userData.map((entry, index) => {
      if (index === 0 && Number(entry.week) < 5) {
        return {
          ...entry,
          week: firstProjectionWeek,
        };
      }
      return entry;
    });

    const merged = [];

    for (let i = 0; i < projection.length; i++) {
      const week = projection[i].week;

      const user =
        normalizedUserData.find(
          (entry) => Number(entry.week) === Number(week)
        ) || {};

      const isFahrenheit = unit === "F";

      merged.push({
        week,
        range: [
          projection[i].min,
          projection[i].max,
        ],

        ...(isFahrenheit
          ? {
            Temp: user.temp,
            unit: "F",
          }
          : {
            Temp: user.tempCelsius,
            unit: "C",
          }),
      });
    }

    return merged;
  };

  const mergedData = mergeRange(data, projection)
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isXLUp = useMediaQuery(theme.breakpoints.up("xl"));
  const chartData =
    patientType === "GYNECOLOGY"
      ? buildGynecologyTemperatureData(
        data,
        projection,
        unit
      )
      : mergeRange(data, projection);

  const firstDate =
    patientType === "GYNECOLOGY"
      ? chartData?.[0]?.fullDate
      : undefined;

  const lastDate =
    patientType === "GYNECOLOGY"
      ? chartData?.[chartData.length - 1]?.fullDate
      : undefined;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const tempEntry = payload.find(
      (entry) => entry.dataKey === "Temp"
    );

    if (!tempEntry || tempEntry.value == null) {
      return null;
    }

    return (
      <div
        style={{
          background: "#fff",
          padding: "6px 10px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          fontSize: "12px",
        }}
      >
        {patientType === "GYNECOLOGY" && label && (
          <div
            style={{
              fontWeight: 700,
              marginBottom: "4px",
            }}
          >
            {dayjs(Number(label)).format("DD MMM YYYY")}
          </div>
        )}

        <div style={{ fontWeight: 600 }}>
          Temp: {tempEntry.value} °{unit}
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%" >
      <ComposedChart data={chartData} margin={{ top: 2, right: isXLUp ? 30 : isLgUp ? 15 : 15, left: isXLUp ? 5 : isLgUp ? 4 : 4, bottom: 25 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />


        {patientType === "GYNECOLOGY" ? (
          <XAxis
            dataKey="fullDate"
            type="number"
            scale="time"
            domain={
              firstDate && lastDate
                  ? [firstDate, lastDate]
                  : ["dataMin", "dataMax"]
          }
            tickFormatter={(value) =>
              dayjs(value).format("DD MMM")
            }
            tick={{
              fill: "#000",
              fontSize: "8px",
              fontWeight: "bold",
            }}
          />
        ) : (
          <XAxis
            dataKey="week"
            label={{
              value: "Week",
              fontWeight: "bold",
              position:  "insideBottomRight", 
              dy: 10, 
              dx: -5,
              fontSize: "10px",
              fill: "#000",
            }}
            tick={{
              fill: "#000",
              fontSize: "8px",
              fontWeight: "bold",
            }}
          />
        )}
        <YAxis
          label={{
            value: `Temp (°${unit})`, angle: -90, position: "insideLeft", fontWeight: 'bold', fontSize: '10px', style: {
              textAnchor: "middle", fill: "#000",
            },dx: 15,
          }}
          axisLine={false}
          tickLine={false}
          domain={unit == "F" ? [90, 110] : [20, 50]}
          tick={{ fill: "#000", fontSize: '8px', fontWeight: 'bold' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          dataKey="range"
          fill="#E6EAEA"
          stroke="#E6EAEA"
          legendType="none"
          connectNulls
        />
        <Legend
          verticalAlign="top"
          align="center"
          iconType="circle"
          iconSize={6}
          wrapperStyle={{
            paddingBottom: '10px',
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 1)',
          }}
          formatter={(value) => {
            let color = "rgba(0, 0, 0, 1)";
            return <span style={{ color }}>{value}</span>;
          }}
        />
        <Line
          type="monotone"
          dataKey="Temp"
          stroke="#5E17EB"
          strokeWidth={1}
          dot
          fill="#5E17EB"
          fillOpacity={0.4}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
